import React, {useRef, useState} from 'react';
import styled, {css} from 'styled-components';

/**
 * @typedef {'top' | 'bottom' | 'left' | 'right'} Direction
 */

const OFFSET = '0.50rem';
const ARROW_SIZE = 6;
const POPOVER_BG_COLOR = 'rgba(0, 0, 0, 0.75)';
const Z_INDEX_POPOVER = 100;

const Wrapper = styled.div`
    position: relative;
    display: inline-block;
`;

const Popover = styled.div`
    position: absolute;
    padding: 8px 12px;
    background-color: ${POPOVER_BG_COLOR};
    color: #fff;
    border-radius: 4px;
    font-size: 0.875rem;
    white-space: nowrap;
    z-index: ${Z_INDEX_POPOVER};
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease-in-out;

    ${(props) => getPopoverPositionStyles(props.direction, props.adjustX)}
    &::after {
        content: '';
        position: absolute;
        ${(props) => getArrowStyles(props.direction, props.adjustX)}
    }
`;

const PopoverContainer = styled.div`
    &:hover ${Popover} {
        visibility: visible;
        opacity: 1;
        pointer-events: auto;
    }
`;

/**
 * Calculates the popover's absolute position based on direction.
 * Applies an offset to separate it from the trigger.
 *
 * @param {Direction} direction
 * @param adjustX
 * @returns {import('styled-components').FlattenSimpleInterpolation}
 */
const getPopoverPositionStyles = (direction, adjustX = 50) => {
    switch (direction) {
        case 'top':
            return css`
                bottom: calc(100% + ${OFFSET});
                left: 50%;
                transform: translateX(calc(-50% + ${adjustX}px));
            `;
        case 'bottom':
            return css`
                top: calc(100% + ${OFFSET});
                left: 50%;
                transform: translateX(calc(-50% + ${adjustX}px));
            `;
        case 'left':
            return css`
                right: calc(100% + ${OFFSET});
                top: 50%;
                transform: translate(calc(${adjustX}px, -50%));
            `;
        case 'right':
        default:
            return css`
                left: calc(100% + ${OFFSET});
                top: 50%;
                transform: translate(calc(${adjustX}px, -50%));
            `;
    }
};

/**
 * Posición de la flecha.
 * Para top/bottom compensamos el movimiento del popover
 * para que la flecha siga apuntando al trigger.
 */
const getArrowStyles = (direction, adjustX = 0) => {
    switch (direction) {
        case 'top':
            return css`
                top: 100%;
                left: calc(50% - ${adjustX}px);
                transform: translateX(-50%);
                border-width: ${ARROW_SIZE}px;
                border-style: solid;
                border-color: ${POPOVER_BG_COLOR} transparent transparent transparent;
            `;
        case 'bottom':
            return css`
                bottom: 100%;
                left: calc(50% - ${adjustX}px);
                transform: translateX(-50%);
                border-width: ${ARROW_SIZE}px;
                border-style: solid;
                border-color: transparent transparent ${POPOVER_BG_COLOR} transparent;
            `;
        case 'left':
            return css`
                left: 100%;
                top: 50%;
                transform: translateY(-50%);
                border-width: ${ARROW_SIZE}px;
                border-style: solid;
                border-color: transparent transparent transparent ${POPOVER_BG_COLOR};
            `;
        case 'right':
        default:
            return css`
                right: 100%;
                top: 50%;
                transform: translateY(-50%);
                border-width: ${ARROW_SIZE}px;
                border-style: solid;
                border-color: transparent ${POPOVER_BG_COLOR} transparent transparent;
            `;
    }
};

export const HoverPopoverUI = ({message, direction = 'top', children}) => {
    const popoverRef = useRef(null);
    const [adjustX, setAdjustX] = useState(0);

    const handleMouseEnter = () => {
        const el = popoverRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const padding = 8;
        let delta = 0;

        if (rect.left < padding) {
            delta = padding - rect.left;
        } else if (rect.right > window.innerWidth - padding) {
            delta = (window.innerWidth - padding) - rect.right;
        }

        setAdjustX(delta);
    };

    const handleMouseLeave = () => {
        setAdjustX(0);
    };

    return (
        <Wrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <PopoverContainer>
                {children}
                <Popover
                    ref={popoverRef}
                    direction={direction}
                    adjustX={adjustX}
                >
                    {message}
                </Popover>
            </PopoverContainer>
        </Wrapper>
    );
};
